import { GestureHandlerRootView } from "react-native-gesture-handler";
import React, { useEffect, useState } from "react";
import { View, StyleSheet } from "react-native";
import { AppConfigProvider } from "./AppConfigProvider";

import { ApolloProvider } from "@apollo/client";
import { AppStateProvider } from "./contexts/AppStateContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import "react-native-reanimated";
import { Platform } from "react-native";
import { loadFonts } from "./expo-fonts";
import { PaperProvider } from "react-native-paper";
import { PostHogProvider } from "posthog-js/react";
import AppNavigator from "./navigation/AppNavigator.web";
import { AuthProvider } from "./contexts/AuthContext";
import { WindowDimensionsProvider } from "./contexts/WindowDimensionsContext";
import Loader from "./components/common/status/Loader/Loader";
import { AudioPlayerProvider } from "./contexts/AudioPlayerContext";
import { MathJaxContext } from "better-react-mathjax";
import { apolloClient } from "./graphql/graphql.client";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";

import "./styles.web.css";
import { ChatBarProvider } from "./contexts/ChatBarContext";
import { CourseDetailsProvider } from "./contexts/CourseDetailsContext";
import { ChatBarInputProvider } from "./contexts/CharBarInputContext";
import { AccessibilityProvider } from "./contexts/AccessibilityContext";
import { VisitSessionProvider } from "./contexts/VisitSessionContext"; // Import VisitSessionProvider
import Sentry from "./utils/sentry";
import { en, registerTranslation } from "react-native-paper-dates";
import { ActiveCourseProvider } from "./contexts/ActiveCourseContext";
import { PreviewModeProvider } from "./contexts/PreviewModeContext";
import { ToastProvider } from "./contexts/Toast/ToastProvider";
import { WorkspaceThreadsProvider } from "./contexts/WorkspaceThreadsContext";
import { NotificationProvider } from "./contexts/NotificationContext";

registerTranslation("en", en);

if (Platform.OS === "web") {
  global._frameTimestamp = null;
}

const options = {
  api_host: "https://app.posthog.com",
  session_recording: {
    maskTextSelector: '[data-sensitive="true"]',
  },
};

const App = () => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const R = window;
      const a = document;
      const p = "https://js.rapidr.io";
      const i = "sdk.js";

      R.Rapidr =
        R.Rapidr ||
        function () {
          (R.Rapidr.q = R.Rapidr.q || []).push(arguments);
        };

      const d = a.getElementsByTagName("head")[0];
      const r = a.createElement("script");
      r.async = 1;
      r.src = p + "/" + i;
      d.appendChild(r);
    }
  }, []);

  useEffect(() => {
    loadFonts().then(() => setIsReady(true));
  }, []);

  if (!isReady) {
    return (
      <View style={styles.loaderContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <I18nextProvider i18n={i18n}>
        <WindowDimensionsProvider>
          <AppConfigProvider>
            <PostHogProvider apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY} options={options} autocapture>
              <ApolloProvider client={apolloClient}>
                <AppStateProvider>
                  <AuthProvider>
                    <View style={{ flex: 1, overflow: "hidden" }}>
                      <PaperProvider>
                        <WebSocketProvider>
                          <AccessibilityProvider>
                            <AudioPlayerProvider>
                              <MathJaxContext>
                                <PreviewModeProvider>
                                  <ChatBarInputProvider>
                                    <ChatBarProvider>
                                      <CourseDetailsProvider>
                                        <ActiveCourseProvider>
                                          <VisitSessionProvider>
                                            <WorkspaceThreadsProvider>
                                              <ToastProvider>
                                                <NotificationProvider>
                                                  <AppNavigator />
                                                </NotificationProvider>
                                              </ToastProvider>
                                            </WorkspaceThreadsProvider>
                                          </VisitSessionProvider>
                                        </ActiveCourseProvider>
                                      </CourseDetailsProvider>
                                    </ChatBarProvider>
                                  </ChatBarInputProvider>
                                </PreviewModeProvider>
                              </MathJaxContext>
                            </AudioPlayerProvider>
                          </AccessibilityProvider>
                        </WebSocketProvider>
                      </PaperProvider>
                    </View>
                  </AuthProvider>
                </AppStateProvider>
              </ApolloProvider>
            </PostHogProvider>
          </AppConfigProvider>
        </WindowDimensionsProvider>
      </I18nextProvider>
    </GestureHandlerRootView>
  );
};

const AppEntryPoint = App;

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default Sentry.wrap(AppEntryPoint);
